/**
 * Sending a scope's history (git sync Stage 2) — shared by Sync now and Pull
 * (scopeGitSync.js). `merge` is scopeGitSync's mergeAndApply, passed in so
 * this module does not import it back.
 *
 * Pull commits what the app holds before it merges, so it must send that
 * commit too (owner's data, 07.10.2026: a Pull left "4 documents, 7 tasks"
 * unsent and the Git page said "Changes waiting" with nothing edited) — the
 * way VS Code's Sync pulls and then pushes.
 */
import { invoke } from '@tauri-apps/api/core';
import { reportScopeProgress } from './scopeProgress';

const STOPPED = new Set(['conflicts', 'not_applied']);
const PULLED = new Set(['up_to_date', 'fast_forward', 'merged']);

/**
 * Push; when the remote moved meanwhile, merge it in (and apply it), then push again.
 * @returns {Promise<{ status: 'ok'|'diverged'|'conflicts'|'not_applied', applied?, conflicts?, problems? }>}
 */
export async function pushMergingIfDiverged(scope, dir, { cred, branch, author, merge }) {
  reportScopeProgress(scope.key, { step: 'pushing' });
  let push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
  if (push.status !== 'diverged') return { status: 'ok' };
  const merged = await merge(scope, dir, { ...cred, remote: 'origin', branch, ...author });
  if (STOPPED.has(merged.status)) return merged;
  push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
  return { status: push.status === 'ok' ? 'ok' : 'diverged', applied: merged.applied };
}

/**
 * After a pull that ended well: send what is ahead of the remote (the
 * commit the pull made, a merge of both sides). Conflicts / not applied /
 * secrets are returned as they are — nothing is sent. A push that fails
 * keeps the pull: `sent: false` + `sendError`, and the version waits for the
 * next Sync now.
 */
export async function sendAfterPull(scope, dir, pulled, opts) {
  if (!PULLED.has(pulled?.status)) return pulled;
  // git_merge_remote has just fetched: ahead is current.
  const status = await invoke('git_status', { repoPath: dir, remote: 'origin', branch: opts.branch }).catch(() => null);
  if (!((status?.ahead || 0) > 0)) return pulled;
  try {
    const sent = await pushMergingIfDiverged(scope, dir, opts);
    if (STOPPED.has(sent.status)) return sent;
    return { ...pulled, applied: pulled.applied || sent.applied, sent: sent.status === 'ok' };
  } catch (err) {
    return { ...pulled, sent: false, sendError: err?.message || String(err) };
  }
}
